import React, { useState } from 'react';
import { Lock, ArrowUp, Check, CreditCard, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import PaymentGateway from './PaymentGateway';
import { companyRegistrationApi, paymentsApi, profileApi } from '../services/api';
import { getErrorMessage } from '../lib/utils';

// Feature display names
const featureNames = {
  dashboard: 'Dashboard',
  directory: 'Member Directory',
  quotes: 'Quote Tool',
  events: 'Event Connect',
  messaging: 'Direct Messaging',
  rewards: 'Rewards Program',
  analytics: 'Analytics Dashboard',
  ma_portal: 'M&A Portal',
  referrals: 'Referral Program',
  team: 'Team Management',
  complaints: 'Complaints'
};

const tierOrderRank = {
  silver: 1,
  gold: 2,
  platinum: 3,
  rhodium: 4,
  titanium: 5,
  founder: 6
};

const formatTierLimit = (value, noun) => {
  if (value === 0) return `Unlimited ${noun}`;
  if (value == null || value === '') return `- ${noun}`;
  return `${Number(value).toLocaleString()} ${noun}`;
};

const calculateMonthlyFromAnnual = (annualValue) => {
  const annual = Number(annualValue || 0);
  if (!annual || annual <= 0) return 0;
  return Math.ceil(annual / 12);
};

const formatUsd = (value) => `USD ${Number(value || 0).toLocaleString()}`;

const tierToProductId = {
  founder: 'membership_founder',
  titanium: 'membership_titanium',
  rhodium: 'membership_rhodium',
  platinum: 'membership_platinum',
  gold: 'membership_gold',
  silver: 'membership_silver'
};

const UpgradePrompt = ({ 
  isOpen, 
  onClose, 
  feature = null, // The feature being requested
  quotaType = null, // 'quotes' or 'enquiries' if quota exceeded
  currentUsage = 0,
  limit = 0,
  restrictionMessage = null, // When set, show restriction message instead of upgrade
  onReturnFocus = null
}) => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const { pointsSystemEnabled } = useTier();
  const [selectedTier, setSelectedTier] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentRecord, setPaymentRecord] = useState(null);
  const [companyRegistration, setCompanyRegistration] = useState(null);
  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false);
  const [tiers, setTiers] = useState([]);

  const currentTier = member?.tier?.toLowerCase() || 'silver';
  const currentTierRank = tierOrderRank[currentTier] || 0;
  const availableUpgrades = tiers
    .filter((tier) => {
      const normalizedTier = (tier.id || tier.tier_name || tier.name || '').toLowerCase();
      return (tierOrderRank[normalizedTier] || 0) > currentTierRank;
    })
    .sort((a, b) => Number(a.price_annual ?? a.price ?? 0) - Number(b.price_annual ?? b.price ?? 0));
  const isRegisteredUser = currentTier === 'registered user';
  const isCompanyApproved = companyRegistration?.status?.toLowerCase() === 'approved';
  const shouldBlockPayment = isRegisteredUser && !isCompanyApproved;
  const resolvedFeatureNames = {
    ...featureNames,
    ...(pointsSystemEnabled ? {} : { rewards: 'Member benefits' }),
  };

  React.useEffect(() => {
    let cancelled = false;

    profileApi.getTiers()
      .then((res) => {
        if (!cancelled) {
          setTiers(res.data || []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to load tiers:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    if (!isRegisteredUser) return undefined;

    companyRegistrationApi.getMyRegistration()
      .then((res) => {
        if (!cancelled && res.data?.exists) {
          setCompanyRegistration(res.data);
        }
      })
      .catch((error) => {
        if (!cancelled && error.response?.status !== 404) {
          console.error('Failed to load company registration:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isRegisteredUser]);

  const handleUpgrade = async (tier) => {
    if (shouldBlockPayment) {
      setShowRegistrationDialog(true);
      return;
    }

    setSelectedTier(tier);
    setProcessing(true);
    const tierId = (tier.id || tier.tier_name || tier.name || '').toLowerCase();
    const productId = tierToProductId[tierId] || `membership_${tierId}`;
    try {
      const res = await paymentsApi.createRecord({
        member_id: member?.member_id,
        company_id: member?.company_id,
        product_id: productId
      });
      setPaymentRecord(res.data);
      onClose();
      setShowPaymentDialog(true);
    } catch (err) {
      toast.error(getErrorMessage(err.response?.data?.detail, 'Failed to create payment record'));
    } finally {
      setProcessing(false);
    }
  };

  const getFeatureName = () => {
    if (feature) return resolvedFeatureNames[feature] || feature;
    if (quotaType === 'quotes') return 'Quote Creation';
    if (quotaType === 'enquiries') return 'Enquiry Creation';
    return 'this feature';
  };

  const handlePaymentDialogChange = (open) => {
    setShowPaymentDialog(open);
    if (!open) {
      onReturnFocus?.();
    }
  };

  if (restrictionMessage) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-xl text-red-600">
              <Lock className="mr-2" size={24} />
              Access Restricted
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-red-800">{restrictionMessage}</p>
          </div>
          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const selectedTierName = selectedTier?.display_name || selectedTier?.name || '';

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-xl">
              <Lock className="mr-2 text-amber-500" size={24} />
              Upgrade Required
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Reason for upgrade */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              {quotaType ? (
                <div>
                  <p className="text-amber-800 font-medium">
                    You've reached your monthly {quotaType} limit
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    Used: {currentUsage} / {limit} {quotaType} this month
                  </p>
                </div>
              ) : (
                <p className="text-amber-800">
                  <strong>{getFeatureName()}</strong> is not available on your current <strong>{member?.tier || 'Silver'}</strong> plan.
                  Upgrade to unlock this feature and more!
                </p>
              )}
            </div>

            {/* Billing note */}
            <div className="flex justify-center">
              <div className="inline-flex bg-blue-50 text-blue-700 rounded-lg px-4 py-2 text-sm font-medium">
                Monthly values shown in USD, billed annually
              </div>
            </div>

            {/* Available upgrades */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {availableUpgrades.slice(0, 3).map((tier) => {
                const annualPrice = Number(tier.price_annual ?? tier.price ?? 0);
                const monthlyDisplay = calculateMonthlyFromAnnual(annualPrice);
                const isSelected = selectedTier?.id === tier.id;
                return (
                  <Card 
                    key={tier.id}
                    className={`cursor-pointer transition-all ${
                      isSelected ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'
                    }`}
                    onClick={() => setSelectedTier(tier)}
                  >
                    <CardContent className="p-5">
                      <div className="text-center">
                        <h3 className="font-bold text-lg capitalize">{tier.display_name || tier.name}</h3>
                        <div className="mt-2 flex items-baseline justify-center whitespace-nowrap">
                          <span className="text-2xl lg:text-3xl font-bold">{formatUsd(monthlyDisplay)}</span>
                          <span className="ml-1 text-base text-gray-500">/month</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          billed annually at {formatUsd(annualPrice)}/year
                        </p>
                      </div>
                      
                      <div className="mt-4 space-y-2">
                        {(tier.features || []).map((feat, idx) => (
                          <div key={idx} className="flex items-start text-sm">
                            <Check size={14} className="mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-600">{feat}</span>
                          </div>
                        ))}
                      </div>

                      <div className="pt-4 mt-4 border-t space-y-1 text-sm text-gray-600">
                        <p>📝 {formatTierLimit(tier.max_quotes_per_month, 'quotes/month')}</p>
                        <p>📋 {formatTierLimit(tier.max_enquiries_per_month, 'enquiries/month')}</p>
                        <p>👥 {formatTierLimit(tier.max_team_members, 'team members')}</p>
                      </div>

                      {isSelected && (
                        <div className="mt-4">
                          <Button 
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpgrade(tier);
                            }}
                            disabled={processing}
                          >
                            {processing ? (
                              <>
                                <Loader2 size={16} className="mr-2 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <ArrowUp size={16} className="mr-2" />
                                Upgrade Now
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {availableUpgrades.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-600">You're already on the highest tier!</p>
                <p className="text-sm text-gray-500 mt-2">Contact support if you need additional features.</p>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-between items-center pt-4 border-t">
              <button 
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Maybe later
              </button>
              <Button
                variant="outline"
                onClick={() => {
                  onClose();
                  navigate('/upgrade');
                }}
              >
                <CreditCard size={16} className="mr-2" />
                View All Plans
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Same payment popup flow as Upgrade page */}
      <Dialog open={showPaymentDialog} onOpenChange={handlePaymentDialogChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upgrade to {selectedTierName}</DialogTitle>
          </DialogHeader>
          {selectedTier && paymentRecord && (
            <PaymentGateway
              paymentId={paymentRecord.payment_id}
              amount={paymentRecord.amount ?? Number(selectedTier.price_annual ?? selectedTier.price ?? 0)}
              tierAnnualPrice={Number(selectedTier.price_annual ?? selectedTier.price ?? 0)}
              pricingBreakdown={paymentRecord.pricing_breakdown}
              description={`${selectedTierName} - Annual Membership`}
              productId={tierToProductId[(selectedTier.id || selectedTier.tier_name || selectedTier.name || '').toLowerCase()] || `membership_${(selectedTier.id || selectedTier.tier_name || selectedTier.name || '').toLowerCase()}`}
              gstDetails={paymentRecord.gst_details}
              onSuccess={(result) => {
                handlePaymentDialogChange(false);
                setSelectedTier(null);
                setPaymentRecord(null);
                if (result?.method === 'bank_transfer') {
                  toast.success(`Bank transfer details submitted for ${selectedTierName}. Our team will verify the payment shortly.`);
                  return;
                }
                toast.success(`Payment link opened for ${selectedTierName}. Complete the payment in Razorpay to finish the upgrade.`);
              }}
              onCancel={() => handlePaymentDialogChange(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showRegistrationDialog} onOpenChange={setShowRegistrationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Company Registration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-700">
            <p>
              Registered users stay on the Free tier until company registration is approved.
              Payment is blocked until approval is complete.
            </p>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setShowRegistrationDialog(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpgradePrompt;
